import React from 'react';
import { motion } from 'framer-motion';
import { Globe2, Target, Monitor, Rocket } from 'lucide-react';
import { useParallaxRef } from '@/utils/parallax';
import { useRevealOnce } from '@/hooks/useRevealOnce';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { TracingBeam } from '@/components/lib/aceternity/TracingBeam';

interface FrameworkStep {
  number: string;
  label: string;
  description: string;
  icon: React.ReactNode;
}

const frameworkSteps: FrameworkStep[] = [
  {
    number: '01',
    label: 'Domain',
    description:
      'Start with a memorable, on-brand domain that fits your niche and is easy to say, spell, and share.',
    icon: <Globe2 className="w-6 h-6" />,
  },
  {
    number: '02',
    label: 'Strategy & Brand',
    description:
      "Clarify who you serve, what you offer, and how you're different — then wrap it in a visual identity that instantly signals professionalism.",
    icon: <Target className="w-6 h-6" />,
  },
  {
    number: '03',
    label: 'Website',
    description:
      'Build a focused, conversion-ready website that tells your story and gives visitors a clear path to enquire or book a call.',
    icon: <Monitor className="w-6 h-6" />,
  },
  {
    number: '04',
    label: 'Systems & Launch',
    description:
      'Wire up simple systems (forms, booking, light CRM) and follow a 30–60 day launch plan so your new presence actually gets used.',
    icon: <Rocket className="w-6 h-6" />,
  },
];

export default function ServicesFrameworkSection() {
  // Parallax for subtitle
  const subtitleRef = useParallaxRef({
    speed: -0.12,
    axis: 'y',
    maxTranslate: 10,
  });

  // Parallax for H2 (subtle)
  const titleRef = useParallaxRef({
    speed: -0.06,
    axis: 'y',
    maxTranslate: 6,
  });

  // Reveal hook for section entrance
  const sectionReveal = useRevealOnce({ amount: 0.2 });

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.1,
      },
    },
  };

  const stepVariants = {
    hidden: { opacity: 0, y: 30 },
    show: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: 'easeOut' },
    },
  };

  return (
    <section className="relative w-full bg-black text-slate-100 py-16 md:py-24">
      <div className="container mx-auto px-4">
        <div className="max-w-7xl mx-auto">
          <motion.div
            ref={sectionReveal.ref}
            variants={containerVariants}
            initial="hidden"
            animate={sectionReveal.controls}
          >
            {/* Heading Section */}
            <div className="text-center mb-12 md:mb-16">
              <h2
                ref={titleRef as any}
                className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-slate-50 mb-6"
              >
                Your domain's transformation: the Attain Domains Success Framework
              </h2>
              <p
                ref={subtitleRef as any}
                className="text-lg md:text-xl text-slate-300 leading-relaxed max-w-3xl mx-auto"
              >
                Every project follows the same simple framework so we don't just make something pretty — we build a system that brings in the right clients.
              </p>
            </div>

            {/* Framework Steps with Tracing Beam */}
            <div className="relative">
              <TracingBeam className="px-6">
                <div className="space-y-8 md:space-y-12">
                  {frameworkSteps.map((step, index) => (
                    <motion.div key={index} variants={stepVariants} className="relative">
                      <Card className="bg-slate-900 border-slate-800 hover:border-blue-500/30 transition-all duration-300">
                        <CardHeader className="pb-3">
                          <div className="flex items-start justify-between mb-4">
                            <div className="w-12 h-12 rounded-full bg-blue-500/10 border border-blue-500/20 flex items-center justify-center text-blue-400">
                              {step.icon}
                            </div>
                            <span className="text-xs font-mono text-slate-500">{step.number}</span>
                          </div>
                          <h3 className="text-xl font-semibold text-slate-50">{step.label}</h3>
                        </CardHeader>
                        <CardContent>
                          <p className="text-slate-300 leading-relaxed text-sm">{step.description}</p>
                        </CardContent>
                      </Card>
                    </motion.div>
                  ))}
                </div>
              </TracingBeam>
            </div>

            {/* Optional Bottom Link */}
            <div className="text-center mt-12 md:mt-16">
              <a
                href="#how-it-works"
                className="text-blue-400 hover:text-blue-300 hover:underline text-sm md:text-base transition-colors duration-200"
              >
                Next, we'll look at how we'll work together step-by-step.
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
